import React, { useContext, useEffect } from 'react'
import MediaQuery from 'react-responsive'
import { useHistory } from 'react-router'

import { Spinner } from '@acorns/web-components'
import { useRemoteStrings } from '@acorns/web-utils'
import { useMutation } from '@apollo/react-hooks'
import { compose } from 'recompose'

import { USER_UUID } from 'app-constants'
import { Accessibility } from 'components/Accessibility'
import Button from 'components/Button'
import CenteredSpinner from 'components/CenteredSpinner'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName, SubscribeToTierInput, SubscribeToTierResult } from 'generated/types'
import SelectTier from 'pages/SelectTier'
import { TestIDs } from 'test/utils/testIds'
import themeValues from 'theme'
import trackEvent from 'utils/analytics/trackEvent'
import { sendToSentry } from 'utils/errors'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext } from 'utils/FunnelContext'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { pricingScreenExperiment } from 'utils/Optimizely/features'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'
import { setTierName, setTierPrice, setTierPriceIdCookie } from 'utils/TieredRegistration/utils'

import SubscribeToTierMutation from '../gql/SubscribeToTier.mutation.gql'
import { useTierGroupForUser } from '../useTierGroupForUser'
import {
  ContentContainer,
  FooterContainer,
  Image,
  LayoutNoMobilePadding,
  Pill,
  StyledBody,
  StyledButton,
  StyledTextButton,
  StyledTitle,
} from './styled-components'

type Props = {
  ctaLabel: string
  disclaimer: string
  handleNextClick: () => void
  title: string
  header: string
  text: string
} & WithTieredRegistrationProps &
  WithFlowRouterProps &
  WithOptimizelyProps

const TITLE = 'Save & invest for your future'
const BODY = `Get a financial wellness system for you and your family. Invest spare change, save for retirement, bank smarter,
earn more money, grow your knowledge, and much more.`
const FOOTNOTE = 'Card issued by Lincoln Savings Bank, Member FDIC'

const NextButton = withClickTrack<any, any>(TRACKER_IDS.PRICING_INTERSTITIAL.NEXT_BUTTON)(Button)
const CompareTiersButton = withClickTrack<any, any>(
  TRACKER_IDS.SIGNUP.COMPARE_SUBSCRIPTION_TIERS_BUTTON,
)(StyledTextButton)

const enhance = compose<Props, {}>(withFlowRouter, withTieredRegistration, withOptimizely)

type SubscribeToTierInputs = {
  input: SubscribeToTierInput
}

type SubscribeToTierResults = {
  subscribeToTier: SubscribeToTierResult
}

const PricingInterstitial: React.FC<Props> = ({
  route,
  getVariation,
  getOptimizelyExperimentConfig,
  trackOptimizely,
}) => {
  const { isBackEnabled, isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const optimizelyVariant = getVariation(pricingScreenExperiment)
  const history = useHistory()
  const { getRemoteString } = useRemoteStrings()

  const { data: tierGroupForUserData, loading: isTierGroupForUserLoading } = useTierGroupForUser()
  const [subscribeToTierMutation, { loading: isSubscribeToTierLoading }] = useMutation<
    SubscribeToTierResults,
    SubscribeToTierInputs
  >(SubscribeToTierMutation)

  const userId = window.sessionStorage.getItem(USER_UUID)
  const desiredTierPriceId = window.sessionStorage.getItem('ATTR_SRC_TIER_PRICE_ID')

  const preferredTierId = tierGroupForUserData?.tierGroupForUser.preferredTierId
  const tierToShowcase = tierGroupForUserData?.tierGroupForUser.tierOptions.find(option =>
    desiredTierPriceId
      ? option.preferredTierPrice.id === desiredTierPriceId
      : option.tier.id === preferredTierId,
  )
  const subscriptionTierPriceId = tierToShowcase?.preferredTierPrice.id
  const subscriptionTierPrice = tierToShowcase?.preferredTierPrice.amount.value
  const subscriptionTierName = tierToShowcase?.tier.name

  useEffect(() => {
    if (optimizelyVariant) {
      const experimentConfig = getOptimizelyExperimentConfig(pricingScreenExperiment)
      const variationConfig = experimentConfig?.variationsMap[optimizelyVariant]
      if (variationConfig) {
        trackOptimizely('Experiment Viewed')
        trackEvent('Experiment Viewed', {
          eventData: {
            object_name: 'optimizelyExperiment',
            experimentName: experimentConfig.key,
            experimentId: experimentConfig.id,
            variationId: variationConfig.id,
            variationName: variationConfig.key,
          },
        })
      }
    }
  }, [optimizelyVariant])

  const handleNext = async () => {
    setTierName(subscriptionTierName)
    setTierPriceIdCookie(subscriptionTierPriceId)
    setTierPrice(subscriptionTierPrice)

    try {
      const {
        data: { subscribeToTier },
      } = await subscribeToTierMutation({
        variables: {
          input: {
            tierPriceId: subscriptionTierPriceId,
            tierId: tierToShowcase?.tier.id,
            userId,
          },
        },
      })

      if (subscribeToTier.__typename === 'TierSubscription') {
        isDynamicFunnelEnabled
          ? routeToPage(PageName.PricingInterstitial, ActionType.Continue)
          : route({ from: FlowRoute.PricingInterstitial, action: 'continue' })
      }
    } catch (error) {
      sendToSentry(error, {
        message: 'Unable to subscribe user to a tier',
      })
    }
  }

  const handleCompareTiersRedirect = () => {
    route({
      from: FlowRoute.PricingInterstitial,
      action: 'select-tier',
      forwardQueryParams: true,
    })
  }

  const isSweepstakes = history.location.state?.isSweepstakes

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: {
        object_name: 'PricingInterstitial',
        screen: 'pricingInterstitial',
        screen_name: 'pricingInterstitial',
        event_description: 'Screen Viewed Pricing Interstitial',
        object: 'Screen',
        action: 'Viewed',
      },
    })
  }, [])

  if (isTierGroupForUserLoading) {
    return <CenteredSpinner />
  }

  if (optimizelyVariant === 'V1') {
    return <SelectTier />
  }

  return (
    <>
      <MediaQuery maxWidth={themeValues.breakpoints.sm}>
        <Accessibility zIndex={2} />
      </MediaQuery>
      <MediaQuery minWidth={themeValues.breakpoints.sm + 1}>
        <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.PricingInterstitial)} />
      </MediaQuery>

      <LayoutNoMobilePadding>
        <ContentContainer>
          <Image data-testid={TestIDs.PRICING_INTERSTITIAL.IMAGE} />
          {subscriptionTierPrice ? <Pill>{`$${subscriptionTierPrice}/month`}</Pill> : null}
          <StyledTitle>{TITLE}</StyledTitle>
          <StyledBody variant={optimizelyVariant}>
            <span>
              {`${BODY}`}{' '}
              {subscriptionTierPrice ? <b>{`Just $${subscriptionTierPrice}/month`}</b> : null}
            </span>
          </StyledBody>
        </ContentContainer>
        <Separator />
        <StyledButton>
          <NextButton
            data-testid={TestIDs.PRICING_INTERSTITIAL.CTA}
            onPress={handleNext}
            disabled={isSubscribeToTierLoading}
          >
            {isSubscribeToTierLoading ? (
              <Spinner height={20} width={20} white={true} />
            ) : (
              getRemoteString({ key: 'registration.tier_selection.cta.primary' }) ||
              'Select this tier'
            )}
          </NextButton>
          {!isSweepstakes && (
            <CompareTiersButton
              data-testid={TestIDs.PRICING_INTERSTITIAL.COMPARE_TIERS_BUTTON}
              styleType="flat"
              secondary
              onPress={handleCompareTiersRedirect}
            >
              Compare subscription tiers
            </CompareTiersButton>
          )}
          <FooterContainer>{FOOTNOTE}</FooterContainer>
        </StyledButton>
      </LayoutNoMobilePadding>
    </>
  )
}

export default enhance(PricingInterstitial)
